/**
 * OutreachPanel Component
 * Allows sponsors to send SMS, email, and fax communications to CBOs
 */

import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  Mail,
  MessageSquare,
  Phone,
  Send,
  CheckCircle,
  AlertCircle,
  Eye,
  FileText,
  Loader2
} from 'lucide-react';

interface OutreachPanelProps {
  invitationId: number;
  cboName: string;
  cboEmail?: string;
  cboPhone?: string;
  cboFax?: string;
  onOutreachSent?: () => void;
}

interface OutreachResult {
  channel: string;
  success: boolean;
  error?: string;
  simulated?: boolean;
}

interface OutreachTemplate {
  id: string;
  name: string;
  channels: string[];
  sms_template?: string;
  email_subject?: string;
  email_body?: string;
}

export const OutreachPanel: React.FC<OutreachPanelProps> = ({
  invitationId,
  cboName,
  cboEmail: initialEmail,
  cboPhone: initialPhone,
  cboFax: initialFax,
  onOutreachSent
}) => {
  const { t } = useAppTranslation();
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState<OutreachResult[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Templates
  const [templates, setTemplates] = useState<OutreachTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('program_invitation');
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Organization contact info
  const [cboEmail, setCboEmail] = useState<string | undefined>(initialEmail);
  const [cboPhone, setCboPhone] = useState<string | undefined>(initialPhone);
  const [cboFax, setCboFax] = useState<string | undefined>(initialFax);
  const [loadingOrgInfo, setLoadingOrgInfo] = useState(false);

  // Load templates and org info on mount
  React.useEffect(() => {
    loadTemplates();
    if (!initialEmail && !initialPhone) {
      loadOrganizationInfo();
    }
  }, [invitationId]);

  const loadOrganizationInfo = async () => {
    try {
      setLoadingOrgInfo(true);
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      const response = await fetch(
        getWeaveinApiUrl(`/api/sponsor-invitations/${invitationId}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId || ''
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCboEmail(data.cbo_email || data.contact_email);
        setCboPhone(data.cbo_phone || data.contact_phone);
        setCboFax(data.cbo_fax || data.contact_fax);
      }
    } catch (err) {
      console.error('Error loading organization info:', err);
    } finally {
      setLoadingOrgInfo(false);
    }
  };

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      const response = await fetch(
        getWeaveinApiUrl('/api/cbo-outreach/templates'),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId || ''
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (err) {
      console.error('Error loading templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const channels = [
    {
      id: 'sms',
      name: 'SMS',
      icon: MessageSquare,
      available: !!cboPhone,
      description: cboPhone || (loadingOrgInfo ? 'Loading...' : 'No phone number')
    },
    {
      id: 'phone',
      name: 'Phone Call',
      icon: Phone,
      available: !!cboPhone,
      description: cboPhone || (loadingOrgInfo ? 'Loading...' : 'No phone number')
    },
    {
      id: 'email',
      name: 'Email',
      icon: Mail,
      available: !!cboEmail,
      description: cboEmail || (loadingOrgInfo ? 'Loading...' : 'No email address')
    },
    {
      id: 'fax',
      name: 'Fax',
      icon: FileText,
      available: !!cboFax,
      description: cboFax || (loadingOrgInfo ? 'Loading...' : 'No fax number')
    }
  ];

  const toggleChannel = (channelId: string) => {
    setSelectedChannels(prev =>
      prev.includes(channelId)
        ? prev.filter(c => c !== channelId)
        : [...prev, channelId]
    );
  };

  const handlePreview = async () => {
    if (selectedChannels.length === 0) return;

    try {
      setLoadingPreview(true);
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      const response = await fetch(
        getWeaveinApiUrl(`/api/cbo-outreach/preview?invitation_id=${invitationId}&template=${selectedTemplate}&channel=${selectedChannels[0]}`),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CBO-ID': cboId || ''
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPreview(data);
        setShowPreview(true);
      }
    } catch (err) {
      console.error('Error previewing outreach:', err);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSend = async () => {
    if (selectedChannels.length === 0) return;

    try {
      setSending(true);
      setResults([]);

      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      const response = await fetch(
        getWeaveinApiUrl('/api/cbo-outreach/send'),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CBO-ID': cboId || ''
          },
          body: JSON.stringify({
            invitation_ids: [invitationId],
            channels: selectedChannels,
            custom_message: customMessage || undefined
          })
        }
      );

      if (response.ok) {
        const data = await response.json();

        // Extract results
        if (data.results && data.results.length > 0) {
          const invResult = data.results[0];
          const channelResults: OutreachResult[] = [];

          for (const channel of selectedChannels) {
            const channelData = invResult.results?.[channel];
            channelResults.push({
              channel,
              success: channelData?.success || false,
              error: channelData?.error,
              simulated: channelData?.simulated
            });
          }

          setResults(channelResults);
        }

        if (onOutreachSent) {
          onOutreachSent();
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setResults([{
          channel: 'all',
          success: false,
          error: errorData.detail || 'Failed to send outreach'
        }]);
      }
    } catch (err) {
      setResults([{
        channel: 'all',
        success: false,
        error: err instanceof Error ? err.message : 'Unknown error'
      }]);
    } finally {
      setSending(false);
    }
  };

  const handleSendFollowUp = async () => {
    if (selectedChannels.length === 0) return;

    try {
      setSending(true);
      setResults([]);

      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      const response = await fetch(
        getWeaveinApiUrl('/api/cbo-outreach/follow-up'),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CBO-ID': cboId || ''
          },
          body: JSON.stringify({
            invitation_ids: [invitationId],
            channels: selectedChannels
          })
        }
      );

      if (response.ok) {
        const data = await response.json();

        if (data.results && data.results.length > 0) {
          const invResult = data.results[0];
          const channelResults: OutreachResult[] = [];

          for (const channel of selectedChannels) {
            const channelData = invResult.results?.[channel];
            channelResults.push({
              channel,
              success: channelData?.success || false,
              error: channelData?.error,
              simulated: channelData?.simulated
            });
          }

          setResults(channelResults);
        }

        if (onOutreachSent) {
          onOutreachSent();
        }
      }
    } catch (err) {
      setResults([{
        channel: 'all',
        success: false,
        error: err instanceof Error ? err.message : 'Unknown error'
      }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="p-4 border-blue-200 bg-blue-50/50">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-sm">Send Outreach to {cboName}</h4>
          <Badge variant="outline" className="text-xs">
            {t('sponsors.communication', 'Communication')}
          </Badge>
        </div>

        {/* Template Selection */}
        {templates.length > 0 && (
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('sponsors.message_template', 'Message Template')}
            </label>
            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Channel Selection */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">
            {t('sponsors.communication_channels', 'Communication Channels')}
          </label>
          <div className="flex flex-wrap gap-2">
            {channels.map(channel => (
              <button
                key={channel.id}
                onClick={() => channel.available && toggleChannel(channel.id)}
                disabled={!channel.available}
                className={`
                  flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all
                  ${selectedChannels.includes(channel.id)
                    ? 'border-blue-500 bg-blue-100 text-blue-700'
                    : channel.available
                      ? 'border-gray-300 hover:border-gray-400 text-gray-700'
                      : 'border-gray-200 text-gray-400 cursor-not-allowed'
                  }
                `}
                title={channel.description}
              >
                <channel.icon className="w-4 h-4" />
                {channel.name}
                {!channel.available && (
                  <span className="text-xs text-gray-400">(N/A)</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Custom Message */}
        {selectedChannels.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Custom Message (optional)
            </label>
            <Textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder={t('sponsors.leave_empty_to_use_default', 'Leave empty to use default template...')}
              rows={3}
              className="text-sm"
            />
          </div>
        )}

        {/* Results */}
        {results.length > 0 && (
          <div className="space-y-2">
            {results.map((result, idx) => (
              <div
                key={idx}
                className={`flex items-center gap-2 text-sm p-2 rounded ${
                  result.success
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {result.success ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <AlertCircle className="w-4 h-4" />
                )}
                <span className="font-medium capitalize">{result.channel}:</span>
                {result.success ? (
                  result.simulated ? 'Simulated (service not configured)' : 'Sent successfully'
                ) : (
                  result.error || 'Failed'
                )}
              </div>
            ))}
          </div>
        )}

        {/* Preview Modal */}
        {showPreview && preview && (
          <div className="border rounded-lg p-3 bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('sponsors.preview', 'Preview')}</span>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                &times;
              </button>
            </div>
            {preview.channel === 'sms' ? (
              <div className="text-sm bg-gray-100 p-2 rounded whitespace-pre-wrap">
                {preview.preview}
              </div>
            ) : (
              <div className="text-sm">
                <div className="font-medium mb-1">{preview.preview?.subject}</div>
                <div className="bg-gray-100 p-2 rounded text-xs whitespace-pre-wrap max-h-40 overflow-y-auto">
                  {preview.preview?.body}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            onClick={handleSend}
            disabled={selectedChannels.length === 0 || sending}
            size="sm"
            className="flex items-center gap-2"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            Send Invitation
          </Button>

          <Button
            onClick={handleSendFollowUp}
            disabled={selectedChannels.length === 0 || sending}
            size="sm"
            variant="outline"
            className="flex items-center gap-2"
          >
            <Phone className="w-4 h-4" />
            {t('sponsors.follow_up', 'Follow Up')}
          </Button>

          <Button
            onClick={handlePreview}
            disabled={selectedChannels.length === 0 || loadingPreview}
            size="sm"
            variant="ghost"
            className="flex items-center gap-2"
          >
            {loadingPreview ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
            Preview
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default OutreachPanel;
